import type { ReactElement, ReactNode } from 'react'
import { Callout } from './Callout.tsx'
import { EmptyState } from './EmptyState.tsx'
import { Spinner } from './Spinner.tsx'
import { cellClassName } from './table-columns.ts'
import type { Column } from './table-columns.ts'

/**
 * The `<tbody>` of `DataTable`: the error / loading / empty trio and the
 * rows themselves.
 *
 * Split from `DataTable` to keep both under the size caps, and the seam
 * is a real one — this module owns what goes INSIDE the table, the other
 * owns the table's own shape (columns, widths, sticky header, footer).
 */

export interface DataTableBodyProps<T> {
  columns: readonly Column<T>[]
  rows: readonly T[]
  getRowKey: (row: T) => string
  isLoading: boolean
  error: string | null
  empty: ReactNode
  onRowClick: ((row: T) => void) | undefined
  getRowLabel: ((row: T) => string) | undefined
  selectedRowKey: string | null
}

export function DataTableBody<T>({
  columns,
  rows,
  getRowKey,
  isLoading,
  error,
  empty,
  onRowClick,
  getRowLabel,
  selectedRowKey,
}: DataTableBodyProps<T>): ReactElement {
  const colSpan = columns.length

  // Error beats loading beats empty. A failed fetch rendered as "no rows"
  // reads to an operator as "all clear", which is the worst lie this app
  // can tell — several screens used to do exactly that, having no error
  // state at all.
  if (error !== null && error !== '') {
    return (
      <tbody>
        <StateRow colSpan={colSpan}>
          <Callout tone="danger" title="Could not load">
            {error}
          </Callout>
        </StateRow>
      </tbody>
    )
  }
  if (isLoading) {
    return (
      <tbody>
        <StateRow colSpan={colSpan}>
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted">
            <Spinner label="Loading rows" />
            Loading…
          </div>
        </StateRow>
      </tbody>
    )
  }
  if (rows.length === 0) {
    return (
      <tbody>
        <StateRow colSpan={colSpan}>
          {empty ?? <EmptyState headline="Nothing to show" />}
        </StateRow>
      </tbody>
    )
  }

  return (
    <tbody className="divide-y divide-subtle">
      {rows.map((row) => (
        <DataTableRow
          key={getRowKey(row)}
          row={row}
          columns={columns}
          onRowClick={onRowClick}
          getRowLabel={getRowLabel}
          isSelected={selectedRowKey === getRowKey(row)}
        />
      ))}
    </tbody>
  )
}

/**
 * One row.
 *
 * Activation is a real `<button>` in the first cell, never an `onClick`
 * on the `<tr>` alone. A clickable row with no focusable control is
 * unreachable by keyboard, and `tabIndex`/`role="button"` on a `<tr>`
 * destroys the table semantics that make the rest of the grid navigable.
 * The row's own `onClick` is kept purely as a pointer convenience that
 * duplicates the button.
 */
function DataTableRow<T>({
  row,
  columns,
  onRowClick,
  getRowLabel,
  isSelected,
}: {
  row: T
  columns: readonly Column<T>[]
  onRowClick: ((row: T) => void) | undefined
  getRowLabel: ((row: T) => string) | undefined
  isSelected: boolean
}): ReactElement {
  return (
    <tr
      onClick={
        onRowClick === undefined
          ? undefined
          : () => {
              onRowClick(row)
            }
      }
      className={`h-row ${isSelected ? 'bg-elevated' : ''} ${
        onRowClick === undefined
          ? ''
          : 'cursor-pointer transition-colors hover:bg-elevated'
      }`}
    >
      {columns.map((column, index) => (
        <td key={column.key} className={cellClassName(column)}>
          {onRowClick !== undefined && index === 0 ? (
            <button
              type="button"
              aria-label={getRowLabel?.(row)}
              onClick={(event) => {
                // Without this the `<tr>` handler fires a second time for
                // the same activation.
                event.stopPropagation()
                onRowClick(row)
              }}
              className="w-full truncate text-left transition-colors hover:text-accent"
            >
              {column.render(row)}
            </button>
          ) : (
            column.render(row)
          )}
        </td>
      ))}
    </tr>
  )
}

/** One full-width row carrying a state. Kept inside the `<tbody>` rather
 * than replacing the whole table so the header and the column widths stay
 * put — a table that collapses to a centred message and then springs back
 * to a grid makes the page jump on every poll. */
function StateRow({
  colSpan,
  children,
}: {
  colSpan: number
  children: ReactNode
}): ReactElement {
  return (
    <tr>
      <td colSpan={colSpan} className="p-2">
        {children}
      </td>
    </tr>
  )
}
